import {
    getCastellerFullName,
    getCastellerFirstSurname,
    getCastellerSecondSurname,
    getCastellerSearchText,
    cleanNamePart,
    joinNameParts,
} from '../../../utils/castellerName';
import { formatStripeRequirement } from '../../../utils/stripeRequirements';
import {
    CHANGE_TYPE_LABELS,
    GENCAT_STATUS_LABELS,
    LIFECYCLE_LABELS,
    STRIPE_STATUS_LABELS,
    STRIPE_REASON_LABELS,
    WIZARD_STEPS,
} from './constants';

export const stripeStateUnknown = (stripe) => Boolean(stripe?.fetchError || stripe?.reasons?.includes('stripe_unavailable') || stripe?.status === 'not_started');

const MONTHS_CA = ['gener', 'febrer', 'març', 'abril', 'maig', 'juny', 'juliol', 'agost', 'setembre', 'octubre', 'novembre', 'desembre'];

const pad = (value) => String(value).padStart(2, '0');

export const formatDateCa = (value) => {
    if (!value) return '';
    const text = String(value).slice(0, 10);
    const match = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    return `${match[3]}/${match[2]}/${match[1]}`;
};

export const formatLongDateCa = (value) => {
    if (!value) return '';
    const match = String(value).slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    const month = MONTHS_CA[Number(match[2]) - 1];
    if (!month) return '';
    return `${Number(match[3])} ${/^[aeiou]/.test(month) ? 'd\'' : 'de '}${month} de ${match[1]}`;
};

export const formatDateTimeCa = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const todayIso = (now = new Date()) => `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;

export const addYearsIso = (isoDate, years) => {
    const match = String(isoDate || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return '';
    return `${Number(match[1]) + years}-${match[2]}-${match[3]}`;
};

export const changeTypeLabel = (changeType) => CHANGE_TYPE_LABELS[changeType] || changeType || '';
export const lifecycleLabel = (status) => LIFECYCLE_LABELS[status] || status || '';
export const gencatStatusLabel = (status) => GENCAT_STATUS_LABELS[status] || status || '';
export const stripeStatusLabel = (status) => STRIPE_STATUS_LABELS[status] || status || '';

export const gencatTone = (status) => {
    if (status === 'registered') return 'success';
    if (status === 'correction_required') return 'urgent';
    if (status === 'submitted') return 'info';
    return 'warning';
};

export const stripeTone = (status) => {
    if (status === 'verified') return 'success';
    if (status === 'restricted') return 'urgent';
    if (status === 'not_applicable') return 'neutral';
    if (status === 'submitted') return 'info';
    if (status === 'not_started') return 'neutral';
    return 'warning';
};

export const memberFullName = (member) => joinNameParts(member?.legalFirstName, member?.legalLastName) || 'Sense nom';

export const newRequestId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID().replace(/-/g, '').slice(0, 32);
    }
    return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * Build a member row from a casteller of castellersInfo. Names are copied
 * (snapshot) so later edits to the casteller do not rewrite the history.
 * `/api/castellersInfo` exposes the surnames as `cognom` / `segon_cognom`
 * while other payloads use `primer-cognom` / `segon-cognom`; the shared
 * helpers accept every variant.
 */
export const memberFromCasteller = (casteller) => ({
    castellerId: casteller.id,
    legalFirstName: cleanNamePart(casteller.nom),
    legalLastName: joinNameParts(getCastellerFirstSurname(casteller), getCastellerSecondSurname(casteller)),
    roleLabel: '',
});

/**
 * Search castellersInfo by name/mote. Deleted users are already excluded from
 * castellersInfo by the backend; hidden users stay selectable and are flagged.
 */
export const searchCastellers = (castellersInfo, query, { exclude = [], limit = 8 } = {}) => {
    const text = cleanNamePart(query).toLowerCase();
    if (text.length < 2) return [];
    const excluded = new Set(exclude.map(Number));
    return Object.values(castellersInfo || {})
        .filter((casteller) => casteller && casteller.id && !excluded.has(Number(casteller.id)))
        .filter((casteller) => Number(casteller.deleted || 0) !== 1)
        .filter((casteller) => getCastellerSearchText(casteller).toLowerCase().includes(text))
        .sort((a, b) => getCastellerFullName(a).localeCompare(getCastellerFullName(b)))
        .slice(0, limit);
};

/**
 * Change types that make sense (mirror of backend/legal-board/domain.js):
 * the first registration only exists before Aleta knows a current board,
 * and a re-election needs a board to re-elect.
 */
export const allowedChangeTypes = ({ hasCurrent = false } = {}) => (hasCurrent
    ? ['total', 'partial', 'reelection']
    : ['initial', 'total', 'partial']);

/**
 * People worth suggesting when composing the board: the caps de junta of
 * Aleta (`es_junta = 2`). They are only suggestions to add with one click;
 * nothing is pre-filled, because the Aleta role is not the legal board.
 */
export const suggestedCastellers = (castellersInfo, { exclude = [], limit = 12 } = {}) => {
    const excluded = new Set(exclude.map(Number));
    return Object.values(castellersInfo || {})
        .filter((casteller) => casteller && casteller.id && !excluded.has(Number(casteller.id)))
        .filter((casteller) => Number(casteller.deleted || 0) !== 1)
        .filter((casteller) => Number(casteller.es_junta) === 2)
        .sort((a, b) => getCastellerFullName(a).localeCompare(getCastellerFullName(b)))
        .slice(0, limit);
};

/**
 * Decide the wizard step to resume a draft at: dates until there is a date,
 * then the composition.
 */
export const getWizardStepIndex = (term) => {
    if (!term) return 0;
    if (!term.mandateStart && !term.assemblyDate) return 1;
    return 2;
};

// ---------------------------------------------------------------------------
// Chronological steps (Junta a Aleta → Generalitat → Stripe)
// ---------------------------------------------------------------------------

export const STEP_KEYS = Object.freeze({ BOARD: 'board', GENCAT: 'gencat', STRIPE: 'stripe' });

const isoToUtc = (value) => {
    const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!match) return null;
    return Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
};

/** Whole days from `today` (ISO date or Date) to `isoDate`; null when unknown. */
export const daysUntil = (isoDate, today = new Date()) => {
    const target = isoToUtc(isoDate);
    if (target === null) return null;
    const base = typeof today === 'string'
        ? isoToUtc(today)
        : Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
    if (base === null) return null;
    return Math.round((target - base) / 86400000);
};

/**
 * Which of the three steps is the one to act on now. It follows the backend
 * `nextStep` so the UI never disagrees with the badge: the board while there
 * is none (or a draft), otherwise the process the backend points at, or none
 * when everything is done.
 */
export const activeStepKey = (overview) => {
    if (!overview?.current) return STEP_KEYS.BOARD;
    const target = overview?.nextStep?.target;
    if (target === 'gencat') return STEP_KEYS.GENCAT;
    if (target === 'stripe') return STEP_KEYS.STRIPE;
    if (overview?.draft) return STEP_KEYS.BOARD;
    return null;
};

/**
 * State and one-line summary of each step, shown in the accordion headers.
 * States: done · active (open by default) · pending · na (not applicable).
 */
export const computeStepStates = (overview) => {
    const current = overview?.current || null;
    const draft = overview?.draft || null;
    const stripe = overview?.stripe || null;
    const activeKey = activeStepKey(overview);
    const stateFor = (key) => (activeKey === key ? 'active' : 'pending');

    let board;
    if (draft) {
        board = { state: stateFor(STEP_KEYS.BOARD), summary: `Canvi en curs · pas ${getWizardStepIndex(draft) + 1} de ${WIZARD_STEPS.length}` };
    } else if (current) {
        const date = formatDateCa(current.activatedAt);
        board = { state: 'done', summary: date ? `Registrada ${date}` : 'Registrada' };
    } else {
        board = { state: 'active', summary: 'Pendent' };
    }

    let gencat;
    if (!current) {
        gencat = { state: 'pending', summary: 'Després del pas 1' };
    } else if (current.gencatStatus === 'registered') {
        const date = formatDateCa(current.gencatRegisteredAt);
        gencat = { state: 'done', summary: date ? `Inscrita ${date}` : 'Ja constava inscrita' };
    } else if (current.gencatStatus === 'submitted') {
        gencat = { state: stateFor(STEP_KEYS.GENCAT), summary: `Presentada ${formatDateCa(current.gencatSubmittedAt)}`.trim() };
    } else if (current.gencatStatus === 'correction_required') {
        gencat = { state: stateFor(STEP_KEYS.GENCAT), summary: 'Cal corregir documentació' };
    } else {
        gencat = { state: stateFor(STEP_KEYS.GENCAT), summary: 'Pendent' };
    }

    let stripeStep;
    const stripeStatus = stripe?.status || current?.stripeStatus;
    if (!current) {
        stripeStep = { state: 'pending', summary: 'Després del pas 2' };
    } else if (stripe && stripe.connected === false) {
        stripeStep = { state: 'na', summary: 'Sense compte · no cal' };
    } else if (stripeStateUnknown(stripe)) {
        stripeStep = { state: stateFor(STEP_KEYS.STRIPE), summary: 'Estat pendent de comprovar' };
    } else if (stripeStatus === 'verified') {
        const date = formatDateCa(current.stripeVerifiedAt);
        stripeStep = { state: 'done', summary: date ? `Verificat ${date}` : 'Verificat per Stripe' };
    } else if (stripe?.restrictedSoon && stripeDeadline(stripe, overview?.today)) {
        const deadline = stripeDeadline(stripe, overview?.today);
        stripeStep = {
            state: stateFor(STEP_KEYS.STRIPE),
            summary: deadline.daysLeft !== null && deadline.daysLeft < 0
                ? `Data límit superada (${deadline.short})`
                : `Abans del ${deadline.short}${deadline.daysLeft !== null ? ` · ${describeDaysLeft(deadline.daysLeft)}` : ''}`,
        };
    } else {
        stripeStep = { state: stateFor(STEP_KEYS.STRIPE), summary: stripeStatusLabel(stripeStatus) || 'Pendent' };
    }

    return { activeKey, steps: { board, gencat, stripe: stripeStep } };
};

// ---------------------------------------------------------------------------
// Stripe deadline ("Restricted soon") and what stops
// ---------------------------------------------------------------------------

/** Mirror of backend/legal-board/constants.js: the deadline is urgent at 14 days or less. */
export const STRIPE_DEADLINE_URGENT_DAYS = 14;

export const describeDaysLeft = (daysLeft) => {
    if (daysLeft === 0) return 'avui';
    if (daysLeft === 1) return '1 dia';
    return `${daysLeft} dies`;
};

/**
 * Stripe's `requirements.current_deadline` as the UI shows it. The backend
 * sends the ISO date and the days left; older backends only sent the unix
 * timestamp, so both are derived here when missing. `null` without deadline.
 */
export const stripeDeadline = (stripe, today = new Date()) => {
    let isoDate = String(stripe?.deadlineDate || '').slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) {
        const seconds = Number(stripe?.deadline);
        if (!Number.isFinite(seconds) || seconds <= 0) return null;
        const date = new Date(seconds * 1000);
        if (Number.isNaN(date.getTime())) return null;
        isoDate = date.toISOString().slice(0, 10);
    }
    const daysLeft = typeof stripe?.daysLeft === 'number' ? stripe.daysLeft : daysUntil(isoDate, today || new Date());
    return {
        isoDate,
        short: formatDateCa(isoDate),
        long: formatLongDateCa(isoDate),
        daysLeft,
        urgent: daysLeft !== null && daysLeft <= STRIPE_DEADLINE_URGENT_DAYS,
    };
};

const STRIPE_CHARGES = 'els cobraments amb targeta';
const STRIPE_PAYOUTS = 'les transferències a la colla';

/**
 * One sentence on what Stripe has stopped (restricted) or will stop on the
 * deadline (restricted soon). `null` when nothing is at stake.
 */
export const describeStripeStop = (stripe, today = new Date()) => {
    if (!stripe) return null;
    if (stripe.status === 'restricted') {
        const charges = stripe.chargesEnabled === false;
        const payouts = stripe.payoutsEnabled === false;
        if (charges && payouts) return `Stripe ha aturat ${STRIPE_CHARGES} i ${STRIPE_PAYOUTS}. Completa la verificació per reactivar-los.`;
        if (charges) return `Stripe ha aturat ${STRIPE_CHARGES}. Completa la verificació per reactivar-los.`;
        if (payouts) return `Stripe ha aturat ${STRIPE_PAYOUTS}. Completa la verificació per reactivar-les.`;
        return 'Stripe ha restringit el compte fins que es completi la verificació.';
    }
    if (!stripe.restrictedSoon) return null;
    const deadline = stripeDeadline(stripe, today);
    if (!deadline) return null;
    if (deadline.daysLeft !== null && deadline.daysLeft < 0) {
        return `La data límit de Stripe (${deadline.long}) ja ha passat: pot aturar ${STRIPE_CHARGES} i ${STRIPE_PAYOUTS} en qualsevol moment.`;
    }
    const inDays = deadline.daysLeft !== null ? ` (${deadline.daysLeft === 0 ? 'avui' : `en ${describeDaysLeft(deadline.daysLeft)}`})` : '';
    return `El ${deadline.long}${inDays} Stripe aturarà ${STRIPE_CHARGES} i ${STRIPE_PAYOUTS} si no es completa la verificació.`;
};

// ---------------------------------------------------------------------------
// Stripe tasks: what to do at the Stripe form, in the order it works
// ---------------------------------------------------------------------------

const stripeKeysOf = (list) => (Array.isArray(list) ? list : []).map(stripeRequirementKey).filter(Boolean);

const isSignerRequirement = (key) => /^documents\.[a-z_]+\.signer(\.person)?$/.test(key);
const isCertificateRequirement = (key) => key.includes('proof_of_registration')
    || key.startsWith('company.verification.document')
    || key.startsWith('company.verification.additional_document')
    || (key.startsWith('documents.') && key.endsWith('.files'));
const isDirectorsRequirement = (key) => key.startsWith('directors.')
    || key.startsWith('executives.')
    || key === 'company.directors_provided'
    || key === 'company.executives_provided'
    || key.startsWith('company.directorship_declaration');

const stripeErrorsOf = (stripe) => (Array.isArray(stripe?.errors) ? stripe.errors : []);
const hasStripeErrorCode = (error, code) => error?.code === code || error?.detailedCode === code;

/**
 * Stripe lists the registry's previous board as missing directors and asks
 * for proof of the current ones (`verification_missing_directors`). The
 * registry certificate is not accepted as proof of registration; signing
 * Stripe's own attestation electronically at the end of the form is.
 */
export const stripeMissingDirectors = (stripe) => stripeErrorsOf(stripe)
    .some((error) => hasStripeErrorCode(error, 'verification_missing_directors'));

/** Plain-language lines for the Stripe errors worth explaining in «Detall de Stripe». */
export const stripeErrorLines = (stripe) => {
    const lines = [];
    const errors = stripeErrorsOf(stripe);
    if (errors.some((error) => String(error?.requirement || '').includes('proof_of_registration')
        && hasStripeErrorCode(error, 'verification_document_type_not_supported'))) {
        lines.push('Stripe ha rebutjat el document pujat com a prova d’inscripció: no accepta aquest tipus de document.');
    }
    return lines;
};

/**
 * Group Stripe's requirement keys into the steps a person actually follows
 * at the Stripe form: have the registry certificate at hand, declare as
 * directors exactly the people named in it, upload it (and say who signed
 * it). With a deadline only the currently due keys count; the rest stays in
 * the details. Anything that does not fit becomes one "other data" step.
 *
 * `newBoardRegisteredAt` is the date the Generalitat registered a board
 * change. When Stripe still identifies at the registry only people outside
 * the board (`registryOutdated`), Stripe is reading the previous board: the
 * fix is a new certificate and declaring the new board, not the names Stripe
 * lists. A stored certificate uploaded before that date shows the old board.
 *
 * With `verification_missing_directors` the last step is signing Stripe's
 * director attestation electronically (`attestation`), not uploading the
 * certificate as proof of registration.
 */
export const buildStripeTasks = (stripe, { certificateDocument = null, newBoardRegisteredAt = null } = {}) => {
    if (!stripe) return [];
    const allKeys = Array.from(new Set([...stripeKeysOf(stripe.personRequirements), ...stripeKeysOf(stripe.otherRequirements)]));
    const currentlyDue = stripeKeysOf(stripe.currentlyDue);
    const keys = stripe.restrictedSoon && currentlyDue.length > 0 ? allKeys.filter((key) => currentlyDue.includes(key)) : allKeys;
    if (keys.length === 0) return [];

    const certificate = keys.filter(isCertificateRequirement);
    const signer = keys.filter(isSignerRequirement);
    const directors = keys.filter(isDirectorsRequirement);
    const other = keys.filter((key) => !isCertificateRequirement(key) && !isSignerRequirement(key) && !isDirectorsRequirement(key));

    const identifiedDirectors = Array.isArray(stripe.identifiedDirectors) ? stripe.identifiedDirectors : [];
    const registeredAt = String(newBoardRegisteredAt || '').slice(0, 10) || null;
    const registryOutdated = Boolean(registeredAt) && identifiedDirectors.length > 0 && identifiedDirectors.every((director) => !director.inBoard);
    const uploadedAt = String(certificateDocument?.uploadedAt || '').slice(0, 10);
    const outdatedDocument = Boolean(certificateDocument && registeredAt && uploadedAt && uploadedAt < registeredAt);
    const attestation = stripeMissingDirectors(stripe);

    const tasks = [];
    if (certificate.length > 0) {
        tasks.push({
            key: 'certificate',
            keys: certificate,
            hasDocument: Boolean(certificateDocument),
            documentName: certificateDocument?.name || null,
            documentUploadedAt: certificateDocument?.uploadedAt || null,
            outdatedDocument,
            newBoardRegisteredAt: registeredAt,
        });
    }
    if (directors.length > 0) {
        tasks.push({ key: 'directors', keys: directors, identifiedDirectors, registryOutdated, newBoardRegisteredAt: registeredAt });
    }
    if (certificate.length > 0 || signer.length > 0 || attestation) {
        tasks.push({ key: 'upload', keys: [...certificate, ...signer], withSigner: signer.length > 0, newCertificate: registryOutdated || outdatedDocument, attestation });
    }
    if (other.length > 0) {
        tasks.push({ key: 'other', keys: other, labels: Array.from(new Set(other.map(describeStripeRequirement).filter(Boolean))) });
    }
    return tasks;
};

/**
 * Header of the tab: a status pill, one plain sentence and whether the
 * "prepare a change" action should be promoted (review date approaching).
 */
export const summarizeOverview = (overview) => {
    const current = overview?.current || null;
    const draft = overview?.draft || null;
    const stripe = overview?.stripe || null;
    const reminder = overview?.reminder || null;

    if (!current && !draft) {
        return { pill: { tone: 'warning', label: 'Sense registrar' }, line: 'Encara no hi ha cap junta registrada a Aleta.', renew: false };
    }
    const stripeStatus = stripe?.status || current?.stripeStatus;
    const pending = { tone: 'warning', label: 'Pendent' };
    if (current && stripeStatus === 'restricted') {
        return { pill: { tone: 'urgent', label: 'Cal actuar' }, line: describeStripeStop({ ...stripe, status: 'restricted' }) || 'Stripe ha restringit el compte fins que es completi la verificació.', renew: false };
    }
    // "Restricted soon": the account works today, Stripe stops it on the deadline.
    if (current && stripe?.restrictedSoon && describeStripeStop(stripe, overview?.today)) {
        const deadline = stripeDeadline(stripe, overview?.today);
        return { pill: { tone: deadline?.urgent ? 'urgent' : 'warning', label: 'Cal actuar' }, line: describeStripeStop(stripe, overview?.today), renew: false };
    }
    if (current?.gencatStatus === 'correction_required') {
        return { pill: { tone: 'urgent', label: 'Cal actuar' }, line: 'Junta registrada · la Generalitat demana corregir la documentació.', renew: false };
    }
    if (draft) {
        return { pill: { tone: 'warning', label: 'Canvi en curs' }, line: current ? 'Canvi de junta en curs · la junta vigent continua fins que confirmis la nova.' : 'Canvi de junta en curs.', renew: false };
    }
    if (['not_started', 'preparing'].includes(current.gencatStatus)) {
        return { pill: pending, line: 'Junta registrada · pendent de comunicar al Registre.', renew: false };
    }
    if (stripeStatus === 'action_required') {
        return { pill: pending, line: 'Cal revisar les dades i els requisits de Stripe.', renew: false };
    }
    if (stripeStateUnknown(stripe) || !stripe) {
        return { pill: pending, line: 'No s’ha pogut comprovar tot l’estat de Stripe. Torna-ho a consultar.', renew: false };
    }
    if (reminder && reminder.level !== 'in_progress') {
        return { pill: pending, line: describeReminder(reminder), renew: true };
    }
    if (current.gencatStatus === 'submitted') {
        return { pill: { tone: 'info', label: 'Pendent' }, line: "Junta registrada · presentada al Registre, pendent d'inscripció.", renew: false };
    }
    if (stripeStatus === 'submitted') {
        return { pill: { tone: 'info', label: 'Pendent' }, line: 'Junta inscrita · Stripe està revisant les dades.', renew: false };
    }
    const pill = { tone: 'success', label: 'Vigent' };
    const date = formatDateCa(current.nextReviewDate);
    const days = daysUntil(current.nextReviewDate, overview?.today || new Date());
    const inDays = days !== null && days >= 0 ? ` (en ${days} ${days === 1 ? 'dia' : 'dies'})` : '';
    return { pill, line: date ? `Tot al dia · pròxima revisió ${date}${inDays}` : 'Tot al dia.', renew: false };
};

export const describeReminder = (reminder) => {
    if (!reminder) return null;
    const days = Math.abs(reminder.daysLeft);
    if (reminder.level === 'overdue') {
        return `La data prevista de revisió va passar fa ${days} ${days === 1 ? 'dia' : 'dies'} i no hi ha cap canvi en curs.`;
    }
    if (reminder.level === 'in_progress') {
        return 'La data prevista de revisió ja ha passat. Continua el canvi de junta que tens començat.';
    }
    if (reminder.level === 'urgent') {
        if (days === 0) return 'La revisió de la junta està prevista per avui.';
        return days === 1
            ? 'Queda 1 dia per a la data prevista de revisió.'
            : `Queden ${days} dies per a la data prevista de revisió.`;
    }
    return `Queden ${days} dies per a la data prevista de revisió de la junta.`;
};

/**
 * Human readable summary of an audit event.
 */
export const describeEventDetails = (event) => {
    const details = event?.details || {};
    const parts = [];
    if (event?.type?.startsWith('certificate_document_') && details.name) parts.push(details.name);
    if (details.reference) parts.push(`Referència: ${details.reference}`);
    if (details.date) parts.push(`Data: ${formatDateCa(details.date)}`);
    if (details.note) parts.push(details.note);
    if (details.compositionCorrection) parts.push('Correcció de dades mal introduïdes; la junta no ha canviat.');
    if (details.attested) parts.push('Declarat per la persona que ho registra, no confirmat per la Generalitat.');
    if (details.diff) {
        if (details.diff.added?.length) parts.push(`Entren: ${details.diff.added.join(', ')}`);
        if (details.diff.removed?.length) parts.push(`Surten: ${details.diff.removed.join(', ')}`);
        if (details.diff.roleChanges?.length) parts.push(`Canvis de càrrec: ${details.diff.roleChanges.map((change) => `${change.name} (${change.from} → ${change.to})`).join(', ')}`);
    }
    if (event?.type === 'stripe_status_changed' && details.to) {
        parts.push(`${stripeStatusLabel(details.from)} → ${stripeStatusLabel(details.to)}`);
    }
    if (details.changeType && event?.type === 'term_created') parts.push(changeTypeLabel(details.changeType));
    return parts.join(' · ');
};

export const membersToPayload = (members) => (members || []).map((member, index) => ({
    casteller_id: member.castellerId ?? null,
    legal_first_name: member.legalFirstName || '',
    legal_last_name: member.legalLastName || '',
    role_label: member.roleLabel || '',
    sort_order: index,
}));

// ---------------------------------------------------------------------------
// Client-side composition validation (mirror of backend/legal-board/domain.js)
// ---------------------------------------------------------------------------

const normalizeForComparison = (value) => cleanNamePart(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

export const isPresidencyRole = (roleLabel) => {
    const normalized = normalizeForComparison(roleLabel);
    if (!normalized) return false;
    if (/vice\s*presid/.test(normalized)) return false;
    return /presid/.test(normalized);
};

export const isSecretaryRole = (roleLabel) => /secretar/.test(normalizeForComparison(roleLabel));

/**
 * Immediate feedback for the composition step. The backend runs the same
 * rules and is the source of truth; this only avoids a round trip.
 */
export const validateCompositionClient = (members, { exceptionNote = '' } = {}) => {
    const errors = [];
    const warnings = [];
    const list = Array.isArray(members) ? members : [];
    const hasException = cleanNamePart(exceptionNote).length > 0;

    if (list.length === 0) {
        errors.push({ code: 'NO_MEMBERS', message: 'Cal afegir com a mínim un membre a la junta.' });
        return { valid: false, errors, warnings };
    }

    const seenCastellers = new Set();
    const seenNames = new Set();

    list.forEach((member, index) => {
        const position = index + 1;
        const name = memberFullName(member);
        if (!cleanNamePart(member.legalFirstName) || !cleanNamePart(member.legalLastName)) {
            errors.push({ code: 'MEMBER_NAME_REQUIRED', index, message: `Cal indicar nom i cognoms del membre ${position}.` });
        }
        if (!cleanNamePart(member.roleLabel)) {
            errors.push({ code: 'MEMBER_ROLE_REQUIRED', index, message: `Cal indicar el càrrec de ${name === 'Sense nom' ? `el membre ${position}` : name}.` });
        }

        const castellerId = Number(member.castellerId);
        if (Number.isFinite(castellerId) && castellerId > 0) {
            if (seenCastellers.has(castellerId)) {
                errors.push({ code: 'DUPLICATE_MEMBER', index, message: `${name} apareix més d'una vegada.` });
            }
            seenCastellers.add(castellerId);
        }
        const nameKey = normalizeForComparison(joinNameParts(member.legalFirstName, member.legalLastName));
        if (nameKey) {
            if (seenNames.has(nameKey) && !errors.some((error) => error.code === 'DUPLICATE_MEMBER' && error.index === index)) {
                errors.push({ code: 'DUPLICATE_MEMBER', index, message: `${name} apareix més d'una vegada.` });
            }
            seenNames.add(nameKey);
        }
    });

    [
        { code: 'MISSING_PRESIDENT', check: isPresidencyRole, message: 'La junta ha de tenir una presidència (o copresidència).' },
        { code: 'MISSING_SECRETARY', check: isSecretaryRole, message: 'La junta ha de tenir una secretaria.' },
    ].forEach(({ code, check, message }) => {
        if (list.some((member) => check(member.roleLabel))) return;
        if (hasException) {
            warnings.push({ code, message: `${message} S'ha registrat una excepció estatutària.` });
        } else {
            errors.push({ code, message: `${message} Si els estatuts preveuen una excepció, explica-la a sota.` });
        }
    });

    return { valid: errors.length === 0, errors, warnings };
};

// ---------------------------------------------------------------------------
// Stripe: plain-language descriptions
// ---------------------------------------------------------------------------

const EXTRA_STRIPE_TRANSLATIONS = Object.freeze({
    'company.directors_provided': 'Confirmar que tots els membres de la junta legal (directius) estan declarats',
    'company.executives_provided': 'Confirmar que les persones amb càrrec executiu estan declarades',
    'company.owners_provided': 'Confirmar que no hi ha propietaris (una associació no en té)',
    'company.directorship_declaration.date': 'Declarar la llista de directius a Stripe',
    'company.directorship_declaration.ip': 'Declarar la llista de directius a Stripe',
    'company.ownership_declaration.date': 'Declarar que no hi ha propietaris',
    'company.ownership_declaration.ip': 'Declarar que no hi ha propietaris',
    'directors.dob.day': 'Data de naixement dels directors de l\'entitat',
    'directors.dob.month': 'Data de naixement dels directors de l\'entitat',
    'directors.dob.year': 'Data de naixement dels directors de l\'entitat',
    'directors.address.line1': 'Adreça dels directors de l\'entitat',
    'directors.address.city': 'Ciutat dels directors de l\'entitat',
    'directors.address.postal_code': 'Codi postal dels directors de l\'entitat',
    'directors.id_number': 'DNI/NIE dels directors de l\'entitat',
    'directors.verification.document': 'Document d\'identitat dels directors de l\'entitat',
    'representative.dob.day': 'Data de naixement del representant legal',
    'representative.dob.month': 'Data de naixement del representant legal',
    'representative.dob.year': 'Data de naixement del representant legal',
    'representative.address.line1': 'Adreça del representant legal',
    'representative.id_number': 'DNI/NIE del representant legal',
    'representative.verification.document': 'Document d\'identitat del representant legal',
    'representative.relationship.title': 'Càrrec del representant legal',
});

/**
 * Requirement key of an entry of the backend lists. They are strings, but
 * backends before the `listRequirements` fix also forwarded the objects of
 * Stripe `requirements.errors` (`{ requirement, code, reason }`); read their
 * key instead of printing "[object Object]".
 */
const stripeRequirementKey = (requirement) => {
    const value = typeof requirement === 'string' ? requirement : requirement?.requirement;
    return typeof value === 'string' ? value.trim() : '';
};

export const describeStripeRequirement = (requirement) => {
    const key = stripeRequirementKey(requirement);
    if (!key) return '';
    if (EXTRA_STRIPE_TRANSLATIONS[key]) return EXTRA_STRIPE_TRANSLATIONS[key];
    const translated = formatStripeRequirement(key);
    if (translated !== key) return translated;
    if (key.startsWith('directors.')) return 'Dades dels directors de l\'entitat';
    if (key.startsWith('representative.')) return 'Dades del representant legal';
    if (key.startsWith('company.')) return 'Dades de l\'entitat';
    if (key.startsWith('documents.')) return 'Documents de l\'entitat';
    return key;
};

const formatUnixDeadline = (deadline) => {
    if (!deadline) return '';
    const date = new Date(Number(deadline) * 1000);
    if (Number.isNaN(date.getTime())) return '';
    return formatLongDateCa(todayIso(date));
};

/**
 * Plain-language summary of the Stripe block for people who are not
 * technical: what works, what is stopped, and until when.
 */
export const stripeSummaryLines = (stripe) => {
    if (!stripe || !stripe.connected) {
        return [STRIPE_REASON_LABELS.no_account];
    }
    const lines = [];
    if (stripe.accountInvalid) {
        lines.push(STRIPE_REASON_LABELS.account_invalid);
        return lines;
    }
    if (stripe.fetchError) {
        lines.push(STRIPE_REASON_LABELS.stripe_unavailable);
    }
    if (stripe.chargesEnabled === true) lines.push('Els cobraments amb targeta funcionen.');
    if (stripe.chargesEnabled === false) lines.push(STRIPE_REASON_LABELS.charges_disabled);
    if (stripe.payoutsEnabled === true) lines.push('Els payouts (transferències al compte de la colla) funcionen.');
    if (stripe.payoutsEnabled === false) lines.push(STRIPE_REASON_LABELS.payouts_disabled);

    (stripe.reasons || [])
        .filter((reason) => !['charges_disabled', 'payouts_disabled', 'no_account', 'account_invalid', 'stripe_unavailable'].includes(reason))
        .forEach((reason) => {
            if (STRIPE_REASON_LABELS[reason]) lines.push(STRIPE_REASON_LABELS[reason]);
        });

    const deadline = formatUnixDeadline(stripe.deadline);
    if (deadline) lines.push(`Stripe ha fixat una data límit: ${deadline}.`);

    return lines;
};

export const compositionHasChanged = (previous, next) => {
    const signature = (members) => (members || []).map((member) => {
        const identity = member.castellerId ? `c:${member.castellerId}` : `n:${normalizeForComparison(memberFullName(member))}`;
        return JSON.stringify([identity, normalizeForComparison(memberFullName(member)), normalizeForComparison(member.roleLabel)]);
    }).sort();
    return JSON.stringify(signature(previous)) !== JSON.stringify(signature(next));
};

// A renamed linked row can describe a different person in a real board change.
// Keep name corrections linked only when the user explicitly chooses correction.
export const membersForBoardChange = (previous, next) => (next || []).map(member => {
    const original = (previous || []).find(item => member.castellerId && Number(item.castellerId) === Number(member.castellerId));
    return original && normalizeForComparison(memberFullName(original)) !== normalizeForComparison(memberFullName(member))
        ? { ...member, castellerId: null }
        : member;
});
